import React, { FC, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import MainButton from '@ui-kit/components/mainButton';
import { t } from '@base/utils/helpers';
import styles from '../../styles/common.module.scss';
import { Loader } from '@ui-kit/components/loader';
import { ILocalStorage } from '@base/features/storage/interfaces';
import { ResponseCode } from '@base/utils/constants';
import { IAuthService, ResponseUserAuthData } from '@base/features/auth/model/interfaces';
import { container } from 'tsyringe';
import { regExpPassword, registrationMessages } from '@base/features/auth/model/const';
import { MAIN_ROUTES } from '@base/features/routing/constants/mainRoutes';
import { IModalProvider } from '@base/features/modal/model/interfaces';
import { useForm, FormProvider } from 'react-hook-form';
import FormError from '@ui-kit/components/formError';
import { useDebounce } from '@utils/hooks';
import Tabs, { TabPane } from '@ui-kit/components/tabs';
import { EmailVerification } from '@base/features/auth/components/userVerification/emailVerification';
import { PhoneVerification } from '@base/features/auth/components/userVerification/phoneVerification';
import getMessage from '@base/features/auth/utils/getMessage';
import SocialEntrance from '@base/features/auth/components/socialEntrance';
import PasswordInput from '@ui-kit/components/passwordInput';
import AuthWrapImage from '../authWrapImage';

type PropsType = {
  children?: never;
};

const Registration: FC<PropsType> = () => {
  const authService: IAuthService = container.resolve('authService');
  const modalProvider: IModalProvider = container.resolve('modalProvider');
  const localStorage: ILocalStorage = container.resolve('localStorage');
  const history = useHistory();
  const [isVisibleError, setVisibleError] = useState<boolean>(false);

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      loginValue: '',
      passwordValue: '',
      confPasswordValue: '',
      emailValue: '',
      phoneValue: '',
      recommenderValue: '',
      phoneVerification: {
        token: '',
        regCode: '',
      },
    },
  });
  const {
    register,
    handleSubmit,
    getValues,
    trigger,
    setError,
    clearErrors,
    formState,
    formState: { isSubmitting, errors },
  } = methods;
  const { loginValue, passwordValue } = getValues();

  const debouncedLoginValue = useDebounce(loginValue, 500);
  useEffect(() => {
    async function fetchUsername() {
      if (loginValue) {
        const response = await authService.checkUsernameAvailability(loginValue);
        if (!response) {
          return setError('loginValue', {
            message: t('cannot_connect_to_server'),
          });
        }
        if (response.code === ResponseCode.SUCCESS) {
          return clearErrors('loginValue');
        }
        if (response.code === ResponseCode.NAME_IS_NOT_AVAILABLE) {
          return setError('loginValue', {
            message: t(`Name '${loginValue}' is not available`),
          });
        }
        if (response.code === ResponseCode.NAME_IS_ALREADY_USED) {
          return setError('loginValue', {
            message: t(`Name '${loginValue}' is not available`),
          });
        }
      }
      return;
    }
    fetchUsername();
  }, [debouncedLoginValue, errors.loginValue]);

  function goToLogin() {
    history.push(MAIN_ROUTES.login);
  }

  const registration = handleSubmit(async (data) => {
    if (data.emailValue) {
      localStorage.set('remember_login', loginValue);
      authService
        .registrationWithEmail(data.loginValue, data.passwordValue, data.emailValue, data.recommenderValue)
        .then((res: any) => {
          if (res.code === ResponseCode.SUCCESS) {
            showRegisterModal(res.code);
            goToLogin();
          } else {
            showRegisterModal(res.code);
          }
        })
        .catch(() => {
          modalProvider.show({
            title: t('error'),
            body: t('login_connection_error'),
            buttons: [{ title: t('ok'), onClick: () => modalProvider.hide() }],
          });
        });
    }
    if (data.phoneValue) {
      localStorage.set('remember_login', loginValue);
      authService
        .registrationWithPhone(data.loginValue, data.passwordValue, data.phoneValue, data.recommenderValue)
        .then((res: any) => {
          if (res.code === ResponseCode.SUCCESS) {
            const session = res.session.toString();
            showRegisterModal(res.code);
            setPhone(res.usr, session, data.phoneValue, data.phoneVerification.token);
            goToLogin();
          } else {
            showRegisterModal(res.code);
          }
        })
        .catch(() => {
          modalProvider.show({
            title: t('error'),
            body: t('unknown_error_try_again'),
            buttons: [{ title: t('ok'), onClick: () => modalProvider.hide() }],
          });
        });
    }
  });

  function setPhone(login: string, session: string, phone: string, token: string) {
    authService
      .setUserPhone(login, session, phone, token)
      .then((response: ResponseUserAuthData) => {
        if (response.code === ResponseCode.SUCCESS) {
          console.info('setUserPhone: ', ResponseCode.REQUIRED_PARAMETER_IS_MISSING);
        } else if (response.code === ResponseCode.REQUIRED_PARAMETER_IS_MISSING) {
          console.info('setUserPhone: ', ResponseCode.REQUIRED_PARAMETER_IS_MISSING);
        }
      })
      .catch((e) => {
        console.info('failed_to_send_request', e.message);
      });
  }

  function showRegisterModal(code: number): void {
    const reg = getMessage(code, registrationMessages);
    modalProvider.show({
      title: t(reg.title),
      body: t(reg.message),
      buttons: [{ title: t('ok'), onClick: () => modalProvider.hide() }],
    });
  }

  useEffect(() => {
    trigger('loginValue');
  }, []);

  return (
    <div className="auth">
      {isSubmitting && <Loader className="loader-container--black" />}
      <AuthWrapImage />
      <div className="register-page">
        <h1 className="register-page__title">{t('registration')}</h1>

        <div className="register-content-wrap">
          <div className="register-title">
            <div className="register-title-item--left" />
            <div className="register-title-item">
              <h2>{t('sign_up_with_social_account')}</h2>
            </div>
            <div className="register-title-item--right" />
          </div>

          <div className="register-content">
            <SocialEntrance />
          </div>

          <div className="register-title">
            <div className="register-title-item--left" />
            <div className="register-title-item">
              <h2 className="register-title-item">{t('or_create_your_gc_account')}</h2>
            </div>
            <div className="register-title-item--right" />
          </div>

          <div className="register-content">
            <form className="auth-form" action="">
              <div className="with-error">
                <div className="auth-form-item">
                  <input
                    className={
                      errors.loginValue && loginValue.length
                        ? 'auth-form-item__text'
                        : 'auth-form-item__text auth-form-item__text--valid'
                    }
                    placeholder={t('username')}
                    id="loginValue"
                    type="text"
                    maxLength={20}
                    {...register('loginValue', {
                      required: true,
                      minLength: 3,
                    })}
                    onBlur={() => {
                      trigger('loginValue');
                      if (!loginValue)
                        setError('loginValue', {
                          message: t('username'),
                        });
                    }}
                    onFocus={() => {
                      setVisibleError(true);
                    }}
                  />
                </div>
                {isVisibleError && <FormError errors={formState.errors} field={'loginValue'} />}
              </div>

              <div className="with-error">
                <div className="auth-form-item">
                  <PasswordInput
                    className={
                      errors.passwordValue
                        ? 'auth-form-item__text'
                        : 'auth-form-item__text auth-form-item__text--valid'
                    }
                    placeholder={t('password')}
                    id={'passwordValue'}
                    disabled={!!errors.loginValue}
                    {...register('passwordValue', {
                      required: { value: true, message: t('password') },
                      minLength: {
                        value: 6,
                        message: t('password_6_characters_long'),
                      },
                      validate: {
                        checkPassword: (v) => regExpPassword.test(v) || t('use_only_letters'),
                      },
                    })}
                    onFocus={() => clearErrors('passwordValue')}
                  />
                </div>
                <FormError errors={formState.errors} field={'passwordValue'} />
              </div>

              <div className="with-error">
                <div className="auth-form-item">
                  <PasswordInput
                    className={
                      errors.confPasswordValue
                        ? 'auth-form-item__text'
                        : 'auth-form-item__text auth-form-item__text--valid'
                    }
                    placeholder={t('confirm_password')}
                    id={'confPasswordValue'}
                    disabled={!!errors.loginValue}
                    {...register('confPasswordValue', {
                      required: {
                        value: true,
                        message: t('confirm_password'),
                      },
                      validate: {
                        checkConfirmPassword: (v) => v === passwordValue || t('passwords_not_match'),
                      },
                    })}
                    onFocus={() => clearErrors('confPasswordValue')}
                  />
                </div>
                <FormError errors={formState.errors} field={'confPasswordValue'} />
              </div>
              <div className="with-error">
                <div className="auth-form-item">
                  <input
                    className="auth-form-item__text auth-form-item__text--dont-need-valid"
                    placeholder={t('recommender_optional')}
                    id="recommenderValue"
                    type="text"
                    {...register('recommenderValue')}
                  />
                </div>
              </div>
              <div className="with-error">
                <div className="auth-form-item">
                  <FormProvider {...methods}>
                    <Tabs>
                      <TabPane label={t('phone')} key="1" className={styles.verificationTab}>
                        <PhoneVerification mode="verification" />
                      </TabPane>
                      <TabPane label={t('email')} key="2" className={styles.verificationTab}>
                        <EmailVerification />
                      </TabPane>
                    </Tabs>
                  </FormProvider>
                </div>
              </div>

              <div className="register__btn-wrap">
                <MainButton className="register__btn register__btn-cancel" onClick={goToLogin}>
                  {t('cancel')}
                </MainButton>
                <MainButton
                  type="submit"
                  className="register__btn register__btn-sign-up"
                  disabled={!formState.isValid}
                  onClick={registration}
                >
                  {t('signup')}
                </MainButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Registration;
